import {
  useGetExternalPaymentsSuspense,
  useGetInvoicesSuspense,
} from 'api/payments/generated/fragmentPaymentsAPI';
import {
  PaymentViewHeader,
  PaymentViewLayout,
  PaymentViewLoading,
} from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import React, { Suspense, useMemo } from 'react';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';

import { Amount } from '@fragment/ui/src/components/Amount/Amount';
import { DetailField } from '@fragment/ui/src/components/DetailField/DetailField';
import { Icon } from '@fragment/ui/src/components/Icon/BaseIcon/Icon';

type Props = {
  workspaceId: string;
  transactionId: string;
};

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const ExternalPaymentDetailPageContent = ({
  workspaceId,
  transactionId,
}: Props): JSX.Element => {
  const { data: paymentsData } = useGetExternalPaymentsSuspense();
  const { data: invoicesData } = useGetInvoicesSuspense();
  const [, setLocation] = useLocation();

  // Find the specific payment by transactionId
  const payment = useMemo(
    () => paymentsData.data.find((p) => p.transactionId === transactionId),
    [paymentsData, transactionId]
  );

  // Find the associated invoice
  const invoice = useMemo(
    () => invoicesData.data.find((inv) => inv.id === payment?.invoiceId),
    [invoicesData, payment]
  );

  useDocumentTitle(
    payment ? `Payment ${payment.transactionId}` : 'Payment Detail'
  );

  const handleBackToPayments = () => {
    setLocation(
      makePath(['w', workspaceId, 'payments', 'payments'], { absolute: true })
    );
  };

  const handleNavigateToInvoice = () => {
    if (payment?.invoiceId) {
      setLocation(
        makePath(
          ['w', workspaceId, 'payments', 'invoices', payment.invoiceId],
          {
            absolute: true,
          }
        )
      );
    }
  };

  if (!payment) {
    return (
      <PaymentViewLayout>
        <div className="text-red-500 text-medium">
          Payment not found: {transactionId}
        </div>
      </PaymentViewLayout>
    );
  }

  return (
    <PaymentViewLayout
      header={<PaymentViewHeader>Payment Details</PaymentViewHeader>}
    >
      <div className="flex flex-col w-full h-full">
        <DetailField
          label={
            <button
              type="button"
              onClick={handleBackToPayments}
              className="text-md text-main hover:text-main-500 cursor-pointer"
            >
              ←
            </button>
          }
          padding={false}
        >
          <div className="flex items-center h-full w-fit ml-auto">
            <button
              type="button"
              className="text-md text-main hover:text-main-500 cursor-pointer h-full aspect-square flex items-center justify-center"
              title="Search"
            >
              <Icon type="search" size="md" />
            </button>
          </div>
        </DetailField>

        <DetailField label="Transaction ID" className="w-full">
          <div>{payment.transactionId}</div>
        </DetailField>

        <DetailField label="Amount">
          <div>
            <Amount
              amount={payment.amount}
              currency={{
                code: payment.currency_code,
                precision: 2,
                name: payment.currency_code,
              }}
            />
          </div>
        </DetailField>

        <DetailField label="Account Ref">
          <div>{payment.accountReference}</div>
        </DetailField>

        <DetailField label="Counterparty">
          <div>{payment.counterpartyId}</div>
        </DetailField>

        <DetailField label="Invoice">
          {invoice ? (
            <button
              type="button"
              onClick={handleNavigateToInvoice}
              className="text-main hover:text-main-500 underline cursor-pointer"
            >
              {invoice.ik}
            </button>
          ) : (
            <div className="font-mono text-main-500">{payment.invoiceId}</div>
          )}
        </DetailField>

        <DetailField label="Created">{formatDate(payment.created)}</DetailField>
      </div>
    </PaymentViewLayout>
  );
};

export const ExternalPaymentDetailPage = ({
  workspaceId,
  transactionId,
}: Props): JSX.Element => (
  <Suspense fallback={<PaymentViewLoading message="Loading payment..." />}>
    <ExternalPaymentDetailPageContent
      workspaceId={workspaceId}
      transactionId={transactionId}
    />
  </Suspense>
);
